import React from 'react';
import { TemplateRenderProps } from './types';
import { FormFields, AddonsSelector, CouponSection, TrustAndSecurityBadge, SocialProofAndFomo } from './CheckoutCommonElements';
import { BillingCycleSelector, PlanFeatureList } from './CheckoutSummaryElements';

export const PlayfulTemplate: React.FC<TemplateRenderProps> = ({
  plan,
  config,
  formData,
  onInputChange,
  billingCycle,
  onChangeCycle,
  selectedAddonIds,
  onToggleAddon,
  appliedCoupon,
  onApplyCoupon,
  onRemoveCoupon,
  onSubmitInfo,
  isCapturing,
  currencySymbol,
  addonsTotal,
  discountAmount,
  finalTotal,
}) => {
  return (
    <div
      className="min-h-full p-4 md:p-8 flex items-center justify-center bg-yellow-100/70 text-slate-900"
      style={{
        fontFamily: config.fontFamily || 'Inter, sans-serif',
      }}
    >
      <div className="w-full max-w-4xl bg-white rounded-[2.5rem] border-4 border-slate-900 shadow-[10px_10px_0_0_#4f46e5] overflow-hidden grid grid-cols-1 md:grid-cols-12">
        {/* Left Fun Pink Column */}
        <div className="md:col-span-5 bg-pink-500 text-white p-6 md:p-8 border-b-4 md:border-b-0 md:border-r-4 border-slate-900 flex flex-col justify-between space-y-6">
          <div className="space-y-4">
            <div className="w-12 h-12 bg-white rounded-2xl border-2 border-slate-900 flex items-center justify-center -rotate-3">
              {config.logoUrl ? (
                <img src={config.logoUrl} alt="Logo" className="h-6 object-contain" />
              ) : (
                <svg className="w-6 h-6 text-pink-600 fill-current" viewBox="0 0 24 24">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              )}
            </div>

            <div>
              <span className="bg-yellow-300 text-slate-900 text-[10px] font-black px-2.5 py-0.5 rounded-full uppercase border border-slate-900">
                Premium Plan
              </span>
              <h2 className="text-3xl font-black tracking-tight mt-1">{plan.tier}</h2>
              {plan.description && <p className="text-xs text-pink-100 mt-1 font-medium">{plan.description}</p>}
            </div>

            <BillingCycleSelector billingCycle={billingCycle} onChangeCycle={onChangeCycle} config={config} />
            <PlanFeatureList features={plan.features} textColorClass="text-pink-100" checkColorClass="text-yellow-300" />
          </div>

          <div className="bg-yellow-300 text-slate-900 p-4 rounded-2xl border-2 border-slate-900 shadow-[4px_4px_0_0_#000] rotate-1">
            <p className="text-[10px] uppercase font-black text-slate-700">Total recurring</p>
            <p className="text-3xl font-black">{currencySymbol}{finalTotal.toFixed(2)}</p>
          </div>
        </div>

        {/* Right Form */}
        <div className="md:col-span-7 p-6 md:p-8 space-y-5">
          <div>
            <h1 className="text-2xl font-black text-slate-900">{config.headerMain || 'Join Us Today'}</h1>
            <p className="text-xs text-slate-600">{config.headerSub || 'Fill in details to get started immediately.'}</p>
          </div>

          <SocialProofAndFomo config={config} />

          <form onSubmit={onSubmitInfo} className="space-y-4">
            <FormFields
              config={config}
              formData={formData}
              onInputChange={onInputChange}
              inputClassName="border-2 border-slate-900 bg-slate-50 focus:bg-white rounded-xl font-bold"
            />
            <AddonsSelector addons={config.addons} selectedAddonIds={selectedAddonIds} onToggleAddon={onToggleAddon} currency={plan.currency} />
            <CouponSection config={config} appliedCoupon={appliedCoupon} onApplyCoupon={onApplyCoupon} onRemoveCoupon={onRemoveCoupon} />

            <button
              type="submit"
              disabled={isCapturing}
              className="w-full py-4 text-sm font-black uppercase tracking-wider text-slate-900 bg-emerald-400 border-2 border-slate-900 rounded-2xl shadow-[5px_5px_0_0_#000] hover:translate-y-[-2px] active:translate-y-[0px] transition-all disabled:opacity-50"
            >
              {isCapturing ? 'Setting Up...' : config.buttonText || 'Complete Purchase'}
            </button>
          </form>

          <TrustAndSecurityBadge config={config} />
        </div>
      </div>
    </div>
  );
};
